import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import AppShell from './components/AppShell';
import ConsentBanner from './components/ConsentBanner';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import PasswordResetPage from './pages/PasswordResetPage';
import WelcomePage from './pages/WelcomePage';
import OnboardingPage from './pages/OnboardingPage';
import TierUpListener from './components/TierUpListener';
import { useAuth } from './context/AuthContext';
import { LoadingState } from './components/States';
import { bindServiceWorkerMessages } from './utils/webPush';

// App pages — lazy-loaded so the marketing landing page ships a tiny
// bundle and the app's code splits in only when someone navigates to
// /app. Keeps first-paint on outglow.live snappy.
const FeedPage          = lazy(() => import('./pages/FeedPage'));
const ReelPage          = lazy(() => import('./pages/ReelPage'));
const PostDetailPage    = lazy(() => import('./pages/PostDetailPage'));
const CreatePostPage    = lazy(() => import('./pages/CreatePostPage'));
const BattlesPage       = lazy(() => import('./pages/BattlesPage'));
const BattleDetailPage  = lazy(() => import('./pages/BattleDetailPage'));
const BattleResultPage  = lazy(() => import('./pages/BattleResultPage'));
const CreateBattlePage  = lazy(() => import('./pages/CreateBattlePage'));
const ProfilePage       = lazy(() => import('./pages/ProfilePage'));
const EditProfilePage   = lazy(() => import('./pages/EditProfilePage'));
const FollowersPage     = lazy(() => import('./pages/FollowersPage'));
const SettingsPage      = lazy(() => import('./pages/SettingsPage'));
const PrivacyPage       = lazy(() => import('./pages/PrivacyPage'));
const BlockedUsersPage  = lazy(() => import('./pages/BlockedUsersPage'));
const ChangePasswordPage= lazy(() => import('./pages/ChangePasswordPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const ActivityPage      = lazy(() => import('./pages/ActivityPage'));
const LeaderboardPage   = lazy(() => import('./pages/LeaderboardPage'));
const SearchPage        = lazy(() => import('./pages/SearchPage'));
const CommentPolicyPage  = lazy(() => import('./pages/CommentPolicyPage'));
const CommentFiltersPage = lazy(() => import('./pages/CommentFiltersPage'));
const TwoFactorPage      = lazy(() => import('./pages/TwoFactorPage'));
const SessionsPage       = lazy(() => import('./pages/SessionsPage'));
const InboxPage         = lazy(() => import('./pages/InboxPage'));
const ChatPage          = lazy(() => import('./pages/ChatPage'));
const TopicPage             = lazy(() => import('./pages/TopicPage'));
const CollectionsPage       = lazy(() => import('./pages/CollectionsPage'));
const CollectionDetailPage  = lazy(() => import('./pages/CollectionDetailPage'));
const GroupCreatePage       = lazy(() => import('./pages/GroupCreatePage'));

// Public, crawlable content pages — rendered for logged-out visitors
// (and search / AdSense crawlers) at clean URLs. Logged-in users are
// bounced into the interactive /app equivalents by PublicOrApp below.
const ExplorePage           = lazy(() => import('./pages/PublicContent').then((m) => ({ default: m.ExplorePage })));
const PublicLeaderboardPage = lazy(() => import('./pages/PublicContent').then((m) => ({ default: m.PublicLeaderboardPage })));
const PublicPostPage        = lazy(() => import('./pages/PublicContent').then((m) => ({ default: m.PublicPostPage })));
const PublicProfilePage     = lazy(() => import('./pages/PublicContent').then((m) => ({ default: m.PublicProfilePage })));
const PublicBattlePage      = lazy(() => import('./pages/PublicContent').then((m) => ({ default: m.PublicBattlePage })));
const HowItWorksPage        = lazy(() => import('./pages/ContentPages').then((m) => ({ default: m.HowItWorksPage })));
const AboutPage             = lazy(() => import('./pages/ContentPages').then((m) => ({ default: m.AboutPage })));
const FaqPage               = lazy(() => import('./pages/ContentPages').then((m) => ({ default: m.FaqPage })));
const GuidelinesPage        = lazy(() => import('./pages/ContentPages').then((m) => ({ default: m.GuidelinesPage })));

export default function App() {
  const { bootstrapping } = useAuth();
  const navigate = useNavigate();
  // SW notification clicks post {type:'og:navigate', url} back to
  // the page when an existing tab is reused. Route those through
  // React Router instead of a full reload so the existing app
  // state (auth, scroll position on Arena, etc.) is preserved.
  useEffect(() => bindServiceWorkerMessages(navigate), [navigate]);

  if (bootstrapping) return <LoadingState label="Loading Outglow" />;

  return (
    <>
      <Routes>
        {/* Public */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<PasswordResetPage mode="request" />} />
        <Route path="/reset-password" element={<PasswordResetPage mode="reset" />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/welcome" element={<Protected><WelcomePage /></Protected>} />
        <Route path="/onboarding" element={<Protected><OnboardingPage /></Protected>} />

        {/* Public content surface — crawlable, no auth. The single
            biggest lever for AdSense approval: real publisher content
            at public URLs, with ads sitting next to that content
            instead of on a login wall. */}
        <Route path="/explore"      element={<LazyPage><ExplorePage /></LazyPage>} />
        <Route path="/leaderboard"  element={<LazyPage><PublicLeaderboardPage /></LazyPage>} />
        <Route path="/how-it-works" element={<LazyPage><HowItWorksPage /></LazyPage>} />
        <Route path="/about"        element={<LazyPage><AboutPage /></LazyPage>} />
        <Route path="/faq"          element={<LazyPage><FaqPage /></LazyPage>} />
        <Route path="/guidelines"   element={<LazyPage><GuidelinesPage /></LazyPage>} />

        {/* App (requires auth) */}
        <Route path="/app" element={<Protected><AppShell /></Protected>}>
          <Route index             element={<LazyPage><FeedPage /></LazyPage>} />
          <Route path="reels"      element={<LazyPage><ReelPage /></LazyPage>} />
          <Route path="post/:id"   element={<LazyPage><PostDetailPage /></LazyPage>} />
          <Route path="create"     element={<LazyPage><CreatePostPage /></LazyPage>} />
          <Route path="battles"    element={<LazyPage><BattlesPage /></LazyPage>} />
          <Route path="battles/new" element={<LazyPage><CreateBattlePage /></LazyPage>} />
          <Route path="battles/:id" element={<LazyPage><BattleDetailPage /></LazyPage>} />
          <Route path="battles/:id/result" element={<LazyPage><BattleResultPage /></LazyPage>} />
          <Route path="profile/:id"           element={<LazyPage><ProfilePage /></LazyPage>} />
          <Route path="profile/:id/followers" element={<LazyPage><FollowersPage /></LazyPage>} />
          <Route path="edit-profile" element={<LazyPage><EditProfilePage /></LazyPage>} />
          <Route path="settings"        element={<LazyPage><SettingsPage /></LazyPage>} />
          <Route path="privacy"         element={<LazyPage><PrivacyPage /></LazyPage>} />
          <Route path="blocked-users"   element={<LazyPage><BlockedUsersPage /></LazyPage>} />
          <Route path="change-password" element={<LazyPage><ChangePasswordPage /></LazyPage>} />
          <Route path="notifications" element={<LazyPage><NotificationsPage /></LazyPage>} />
          <Route path="activity"      element={<LazyPage><ActivityPage /></LazyPage>} />
          <Route path="leaderboard"   element={<LazyPage><LeaderboardPage /></LazyPage>} />
          <Route path="search"          element={<LazyPage><SearchPage /></LazyPage>} />
          <Route path="comment-policy"  element={<LazyPage><CommentPolicyPage /></LazyPage>} />
          <Route path="comment-filters" element={<LazyPage><CommentFiltersPage /></LazyPage>} />
          <Route path="two-factor"      element={<LazyPage><TwoFactorPage /></LazyPage>} />
          <Route path="sessions"        element={<LazyPage><SessionsPage /></LazyPage>} />
          <Route path="messages"      element={<LazyPage><InboxPage /></LazyPage>} />
          <Route path="messages/new-group" element={<LazyPage><GroupCreatePage /></LazyPage>} />
          <Route path="messages/:id"  element={<LazyPage><ChatPage /></LazyPage>} />
          <Route path="topic/:tag"          element={<LazyPage><TopicPage /></LazyPage>} />
          <Route path="collections"         element={<LazyPage><CollectionsPage /></LazyPage>} />
          <Route path="collections/:id"     element={<LazyPage><CollectionDetailPage /></LazyPage>} />
        </Route>

        {/* Pretty share URLs (`outglow.live/post/<id>`, no /app/ prefix).
            Logged-OUT visitors and crawlers get a public, read-only
            content view at these URLs — that's what makes the content
            crawlable for SEO + AdSense. Logged-IN users are bounced
            into the interactive /app equivalent so they keep their
            full like/comment/vote experience. The Universal Link /
            App Link layer on iOS / Android intercepts these paths
            BEFORE the browser loads them when the app is installed. */}
        <Route path="/post/:id"    element={<PublicOrApp appBase="/app/post"><PublicPostPage /></PublicOrApp>} />
        <Route path="/profile/:id" element={<PublicOrApp appBase="/app/profile"><PublicProfilePage /></PublicOrApp>} />
        <Route path="/battles/:id" element={<PublicOrApp appBase="/app/battles"><PublicBattlePage /></PublicOrApp>} />
        <Route path="/topic/:tag"      element={<RedirectIntoApp from="/topic" to="/app/topic" />} />
        <Route path="/messages"        element={<Navigate to="/app/messages" replace />} />
        <Route path="/messages/:id"    element={<RedirectIntoApp from="/messages" to="/app/messages" />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Global tier-up celebration — listens for tier_up
          notifications and fires TierUpOverlay whenever one arrives. */}
      <TierUpListener />

      {/* Cookie / ad-consent banner (Google Consent Mode v2). Shows
          once until the visitor chooses; gates ad personalization. */}
      <ConsentBanner />
    </>
  );
}

// Pretty-URL → /app/* redirect. Takes the bare path (post/profile/
// etc.) and forwards into the authenticated app shell at the same
// route, preserving the dynamic segment and any query string /
// hash. Used so share URLs can be short (outglow.live/post/<id>)
// while the SPA's actual routes live under /app.
function RedirectIntoApp({ from, to }) {
  const location = useLocation();
  const remainder = location.pathname.startsWith(from)
    ? location.pathname.slice(from.length)
    : location.pathname;
  return <Navigate to={`${to}${remainder}${location.search}${location.hash}`} replace />;
}

// Share-URL router: logged-out → public read-only content view (the
// crawlable surface); logged-in → the interactive /app route. `appBase`
// is the /app prefix; we splice the dynamic id segment from the current
// path onto it so /post/abc → /app/post/abc.
function PublicOrApp({ appBase, children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user) {
    const id = location.pathname.split('/').filter(Boolean).pop();
    return <Navigate to={`${appBase}/${id}${location.search}${location.hash}`} replace />;
  }
  return <LazyPage>{children}</LazyPage>;
}

function Protected({ children }) {
  const { user, emailLocked } = useAuth();
  const loc = useLocation();
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (emailLocked || (user && user.emailVerified === false)) {
    return <Navigate to="/verify-email" replace />;
  }
  return children;
}

function LazyPage({ children }) {
  return <Suspense fallback={<LoadingState label="Loading…" />}>{children}</Suspense>;
}
